import { makeAutoObservable } from "mobx";
import { CorpSessionError, corpSessionService, } from "@/services/corp/CorpSessionService";
import { getCorpRootStore, resetCorpRootStore } from "@/stores/corp/CorpRootStore";
import { B2B_ENABLED } from "@/utils/featureFlags";
import { decodeJwtPayload } from "@/utils/jwt";
import { getUserRole, isEmployeeActive, isStaffEmployee } from "@/utils/permissions";
import { localizeAuthError } from "@/utils/authErrors";
import { supabase } from "@/utils/supabase";
// Константы для сообщений об ошибках
const ERROR_MESSAGES = {
    SIGNIN_ERROR: "Произошла ошибка при входе",
    OTP_SEND_ERROR: "Произошла ошибка при отправке кода",
    OTP_VERIFY_ERROR: "Произошла ошибка при верификации кода",
    ACCOUNT_NOT_FOUND: "Учётная запись не найдена. Учётные записи создаёт администратор",
};
/**
 * Отказ в доступе корп-админа (D-G3-1, CCR-040): `42501` от `corp_whoami`, либо `401`/`403` — сессия отозвана
 * при блокировке персоналом (`banned_until`), `PGRST301`/`PGRST302` (JWT).
 */
const isCorpAccessRevoked = (error) => error.code === "42501" ||
    error.code === "PGRST301" ||
    error.code === "PGRST302" ||
    error.status === 401 ||
    error.status === 403;
/**
 * Явный `POST /auth/v1/logout` для отозванной сессии корп-админа (D-G3-1, критерий 1 WP-DSP-3-FIX).
 * GoTrue к этому моменту мог уже убрать сессию локально (403 на /auth/v1/user) — `auth.signOut()` тогда не ходит
 * на сервер, поэтому шлём запрос сами с последним известным токеном; ответ (401/403) не важен.
 */
const sendExplicitLogout = async (accessToken) => {
    if (!accessToken)
        return;
    try {
        await supabase().auth.admin.signOut(accessToken, "global");
    }
    catch {
        // сессия уже отозвана сервером
    }
};
// Ошибка «Signups not allowed for otp» от GoTrue: код otp_disabled или текст.
const isOtpDisabledError = (error) => {
    return error.code === "otp_disabled" || /signups not allowed/i.test(error.message);
};
const otpErrorMessage = (error) => {
    return isOtpDisabledError(error) ? ERROR_MESSAGES.ACCOUNT_NOT_FOUND : localizeAuthError(error.message);
};
export class AuthStore {
    constructor() {
        this.user = null;
        this.session = null;
        this.employee = null;
        this.principal = null;
        this.isLoading = true;
        this.authError = null;
        this.isOtpSent = false;
        this.otpMethod = null;
        this.pendingEmail = null;
        this.pendingPhone = null;
        // Разделяемый promise для параллельных вызовов resolvePrincipal (не наблюдаемые поля).
        this.resolving = null;
        this.resolvingUserId = null;
        this.setAuthError = (error) => {
            this.authError = error;
        };
        this.setLoading = (loading) => {
            this.isLoading = loading;
        };
        this.setOtpSent = (sent) => {
            this.isOtpSent = sent;
        };
        this.setOtpMethod = (method) => {
            this.otpMethod = method;
        };
        this.setPendingEmail = (email) => {
            this.pendingEmail = email;
        };
        this.setPendingPhone = (phone) => {
            this.pendingPhone = phone;
        };
        this.resetOtpState = () => {
            this.isOtpSent = false;
            this.otpMethod = null;
            this.pendingEmail = null;
            this.pendingPhone = null;
        };
        this.setUser = (user) => {
            this.user = user;
        };
        this.setSession = (session) => {
            this.session = session;
        };
        this.setPrincipal = (principal) => {
            this.principal = principal;
        };
        /** Закрывает DeniedPage и возвращает пользователя на AuthPage. */
        this.dismissDenied = () => {
            this.setPrincipal(null);
            this.setAuthError(null);
        };
        /**
         * Определяет субъекта (staff | corp | denied) по текущей сессии.
         * Параллельные вызовы для одного пользователя делят один promise (UC1101, п.1).
         */
        this.resolvePrincipal = async ({ trigger, }) => {
            const userId = this.session?.user.id ?? null;
            if (this.resolving && this.resolvingUserId === userId) {
                return this.resolving;
            }
            const run = this.doResolvePrincipal(trigger);
            this.resolving = run;
            this.resolvingUserId = userId;
            try {
                return await run;
            }
            finally {
                if (this.resolving === run) {
                    this.resolving = null;
                    this.resolvingUserId = null;
                }
            }
        };
        this.doResolvePrincipal = async (trigger) => {
            const session = this.session;
            if (!session) {
                this.setPrincipal(null);
                return null;
            }
            const userId = session.user.id;
            const claims = await this.readClaims(session);
            const role = typeof claims?.role === "string" ? claims.role : null;
            const resolution = role === "corporate_admin"
                ? await this.resolveCorpPrincipal(claims, trigger, userId)
                : await this.resolveStaffPrincipal(userId, trigger);
            return await this.applyPrincipal(resolution, userId);
        };
        this.readClaims = async (session) => {
            try {
                const { data, error } = await supabase().auth.getClaims();
                if (!error && data?.claims) {
                    return data.claims;
                }
            }
            catch (error) {
                console.error("Error getting claims:", error);
            }
            return decodeJwtPayload(session.access_token);
        };
        this.resolveCorpPrincipal = async (claims, trigger, userId) => {
            if (!B2B_ENABLED) {
                return { principal: { kind: "denied", reason: "CORP_DISABLED" } };
            }
            const amr = Array.isArray(claims?.amr) ? claims?.amr : [];
            const usedOtp = trigger === "otp" ||
                amr.some((entry) => {
                    const method = entry?.method;
                    return method === "otp" || method === "magiclink";
                });
            if (usedOtp) {
                return { principal: { kind: "denied", reason: "CORP_OTP_FORBIDDEN" } };
            }
            try {
                const corpSession = await corpSessionService.whoami();
                if (corpSession.status === "ACTIVE" && corpSession.companies.length > 0) {
                    return { principal: { kind: "corp" }, employee: null, corpSession };
                }
                return { principal: { kind: "denied", reason: "CORP_BLOCKED" } };
            }
            catch (error) {
                if (error instanceof CorpSessionError && isCorpAccessRevoked(error)) {
                    return { principal: { kind: "denied", reason: "CORP_BLOCKED" } };
                }
                console.error("Error resolving corp principal:", error);
                return { principal: this.resolveTransient(trigger, userId) };
            }
        };
        this.resolveStaffPrincipal = async (userId, trigger) => {
            try {
                const { data, error } = await supabase()
                    .from("employees")
                    .select("*")
                    .eq("authUUID", userId)
                    .eq("isDeleted", false);
                if (error) {
                    // 42501 — окончательный отказ (нет прав), а не временная ошибка (FR-005, правило 4)
                    if (error.code === "42501") {
                        return { principal: { kind: "denied", reason: "NOT_STAFF" } };
                    }
                    throw error;
                }
                const rows = (data ?? []);
                const activeStaffRows = rows.filter(isStaffEmployee);
                if (activeStaffRows.length === 0) {
                    return { principal: { kind: "denied", reason: "NOT_STAFF" } };
                }
                const administrator = activeStaffRows.find((row) => row.role === "ADMINISTRATOR");
                return {
                    principal: { kind: "staff" },
                    employee: administrator ?? activeStaffRows[0],
                };
            }
            catch (error) {
                console.error("Error resolving staff principal:", error);
                return { principal: this.resolveTransient(trigger, userId) };
            }
        };
        // «Временная» ошибка (UC1101, п.6): на фоновом событии сохраняем текущего
        // действующего субъекта того же пользователя, иначе — отказ RESOLVE_FAILED.
        this.resolveTransient = (trigger, userId) => {
            const current = this.principal;
            if (trigger === "event" &&
                current &&
                current.kind !== "denied" &&
                this.user?.id === userId) {
                console.error("Transient error resolving principal; keeping current one");
                return current;
            }
            const reason = "RESOLVE_FAILED";
            return { kind: "denied", reason };
        };
        // Применяет результат проверки. Результат, полученный для другого пользователя
        // (выход или смена сессии во время проверки), отбрасывается. Для denied —
        // сбрасывает сотрудника и корп. сессию и выходит только на этом устройстве
        // (principal остаётся denied, п.7).
        this.applyPrincipal = async (resolution, userId) => {
            if (this.session?.user.id !== userId) {
                return this.principal;
            }
            const { principal } = resolution;
            this.setPrincipal(principal);
            if (resolution.employee !== undefined) {
                this.setEmployee(resolution.employee);
            }
            if (resolution.corpSession) {
                getCorpRootStore().setSession(resolution.corpSession);
            }
            if (principal.kind === "denied") {
                this.setEmployee(null);
                resetCorpRootStore();
                try {
                    // Заблокированный корп-админ: явный POST /auth/v1/logout (D-G3-1); ответ сервера (сессия уже отозвана) не важен.
                    if (principal.reason === "CORP_BLOCKED")
                        await sendExplicitLogout(this.session?.access_token);
                    await supabase().auth.signOut({ scope: "local" });
                }
                catch (error) {
                    console.error("Error signing out after access denial:", error);
                }
                // Сессию чистим сами: при восстановлении подписка на SIGNED_OUT ещё не
                // оформлена, и без этого после «Вернуться ко входу» остался бы старый user.
                this.setSession(null);
                this.setUser(null);
                this.setPrincipal(principal);
            }
            return principal;
        };
        this.initializeAuth = async () => {
            try {
                this.setLoading(true);
                // Получаем текущую сессию
                const { data: { session }, error, } = await supabase().auth.getSession();
                if (error) {
                    console.error("Error getting session:", error);
                }
                else {
                    this.setSession(session);
                    this.setUser(session?.user ?? null);
                    if (session) {
                        await this.resolvePrincipal({ trigger: "restore" });
                    }
                }
                // Подписываемся на изменения состояния аутентификации
                supabase().auth.onAuthStateChange((event, session) => {
                    setTimeout(async () => {
                        if (event === "SIGNED_OUT") {
                            const revokedCorp = this.principal?.kind === "corp" && !this.signingOut;
                            const lastToken = this.session?.access_token;
                            this.setSession(null);
                            this.setUser(null);
                            if (this.principal?.kind !== "denied") {
                                this.setPrincipal(null);
                                this.setEmployee(null);
                                resetCorpRootStore();
                            }
                            if (revokedCorp) {
                                // Сессия корп-админа отозвана без нажатия «Выход» (блокировка персоналом, D-G3-1):
                                // показываем DeniedPage, а не форму входа; явный logout уже отправлен GoTrue/нами.
                                this.setPrincipal({ kind: "denied", reason: "CORP_BLOCKED" });
                                await sendExplicitLogout(lastToken);
                            }
                            return;
                        }
                        // После отказа сессия уже завершена: запоздавшее событие (например,
                        // SIGNED_IN от только что выполненного входа) не должно вернуть старую
                        // сессию и повторно запросить данные (UC1101 BR-8).
                        if (this.principal?.kind === "denied") {
                            return;
                        }
                        // Сессия другого пользователя (например, вход в соседней вкладке):
                        // прежний субъект к ней не относится — до новой проверки показываем загрузку.
                        if (session && this.user && session.user.id !== this.user.id) {
                            this.setPrincipal(null);
                            this.setEmployee(null);
                            resetCorpRootStore();
                        }
                        this.setSession(session);
                        this.setUser(session?.user ?? null);
                        if (session && (event === "SIGNED_IN" || event === "TOKEN_REFRESHED")) {
                            await this.resolvePrincipal({ trigger: "event" });
                        }
                    }, 0);
                });
            }
            catch (error) {
                console.error("Error initializing auth:", error);
            }
            finally {
                this.setLoading(false);
            }
        };
        this.signIn = async (credentials) => {
            try {
                this.setAuthError(null);
                this.setLoading(true);
                const { data, error } = await supabase().auth.signInWithPassword({
                    email: credentials.email,
                    password: credentials.password,
                });
                if (error) {
                    this.setAuthError(localizeAuthError(error.message));
                    return false;
                }
                this.setSession(data.session);
                this.setUser(data.user);
                const principal = await this.resolvePrincipal({ trigger: "password" });
                return principal?.kind !== "denied";
            }
            catch (error) {
                this.setAuthError(`${ERROR_MESSAGES.SIGNIN_ERROR}: ${error.message}`);
                return false;
            }
            finally {
                this.setLoading(false);
            }
        };
        // Выход по инициативе пользователя (кнопка «Выход»)
        /** Пользователь нажал «Выход» — отличаем от отзыва сессии сервером (D-G3-1). */
        this.signingOut = false;
        this.signOut = async () => {
            this.signingOut = true;
            try {
                await supabase().auth.signOut();
                this.setUser(null);
                this.setSession(null);
                this.setPrincipal(null);
                this.setEmployee(null);
                resetCorpRootStore();
                this.resetOtpState();
            }
            catch (error) {
                console.error("Error signing out:", error);
            }
            finally {
                this.signingOut = false;
            }
        };
        // Email OTP методы
        this.signInWithEmailOtp = async (credentials) => {
            try {
                this.setAuthError(null);
                this.setLoading(true);
                const { error } = await supabase().auth.signInWithOtp({
                    email: credentials.email,
                    options: {
                        shouldCreateUser: false,
                    },
                });
                if (error) {
                    this.setAuthError(otpErrorMessage(error));
                    return false;
                }
                this.setPendingEmail(credentials.email);
                this.setOtpMethod("email");
                this.setOtpSent(true);
                return true;
            }
            catch (error) {
                this.setAuthError(ERROR_MESSAGES.OTP_SEND_ERROR);
                return false;
            }
            finally {
                this.setLoading(false);
            }
        };
        // Phone OTP методы
        this.signInWithPhoneOtp = async (credentials) => {
            try {
                this.setAuthError(null);
                this.setLoading(true);
                const { error } = await supabase().auth.signInWithOtp({
                    phone: credentials.phone,
                    options: {
                        shouldCreateUser: false,
                    },
                });
                if (error) {
                    this.setAuthError(otpErrorMessage(error));
                    return false;
                }
                this.setPendingPhone(credentials.phone);
                this.setOtpMethod("phone");
                this.setOtpSent(true);
                return true;
            }
            catch (error) {
                this.setAuthError(ERROR_MESSAGES.OTP_SEND_ERROR);
                return false;
            }
            finally {
                this.setLoading(false);
            }
        };
        // Верификация OTP
        this.verifyOtp = async (credentials) => {
            try {
                this.setAuthError(null);
                this.setLoading(true);
                const verifyParams = credentials.email
                    ? { email: credentials.email, token: credentials.token, type: "email" }
                    : { phone: credentials.phone ?? "", token: credentials.token, type: "sms" };
                const { data, error } = await supabase().auth.verifyOtp(verifyParams);
                if (error) {
                    this.setAuthError(localizeAuthError(error.message));
                    return false;
                }
                this.setSession(data.session);
                this.setUser(data.user);
                this.resetOtpState();
                const principal = await this.resolvePrincipal({ trigger: "otp" });
                return principal?.kind !== "denied";
            }
            catch (error) {
                this.setAuthError(`${ERROR_MESSAGES.OTP_VERIFY_ERROR}: ${error.message}`);
                return false;
            }
            finally {
                this.setLoading(false);
            }
        };
        // Повторная отправка OTP
        this.resendOtp = async () => {
            if (this.otpMethod === "email" && this.pendingEmail) {
                return await this.signInWithEmailOtp({ email: this.pendingEmail });
            }
            else if (this.otpMethod === "phone" && this.pendingPhone) {
                return await this.signInWithPhoneOtp({ phone: this.pendingPhone });
            }
            return false;
        };
        makeAutoObservable(this, {
            resolving: false,
            resolvingUserId: false,
        });
    }
    get isAuthenticated() {
        return !!this.user && !!this.session;
    }
    get userRole() {
        return getUserRole(this.employee);
    }
    get isActiveEmployee() {
        return isEmployeeActive(this.employee);
    }
    // Setter methods для замены runInAction
    setEmployee(employee) {
        this.employee = employee;
    }
}
